import cn from 'classnames';
import { QRCodeSVG } from 'qrcode.react';
import { FC, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useCopyToClipboard } from '@/shared/hooks';
import { Button, Skeleton } from '@/shared/ui';

import { QR_CODE_SIZE } from '../../config';
import { formatSetupKey, parseOtpauthLabel } from '../../lib';
import { MFACodeField } from '../MFACodeField/MFACodeField';

import styles from './MFASetupPanel.module.scss';

type MFASetupPanelProps = {
  otpauthURI: string;
  secret: string;
  code: string;
  isLoading: boolean;
  className?: string;
  description?: ReactNode;
  errorMessage?: ReactNode;
  disabled?: boolean;
  /** Registration reveals the manual key behind a toggle; the other surfaces show it outright. */
  collapsibleSetupKey?: boolean;
  currentCodeDescription?: ReactNode;
  onCodeChange: (code: string) => void;
  onCodeComplete?: (code: string) => void;
};

export const MFASetupPanel: FC<MFASetupPanelProps> = (props) => {
  const {
    otpauthURI,
    secret,
    code,
    isLoading,
    className,
    description,
    errorMessage,
    disabled,
    collapsibleSetupKey,
    currentCodeDescription,
    onCodeChange,
    onCodeComplete,
  } = props;

  const copyToClipboard = useCopyToClipboard();

  const [isSetupKeyVisible, setIsSetupKeyVisible] =
    useState(!collapsibleSetupKey);

  const { issuer, account } = parseOtpauthLabel(otpauthURI);

  return (
    <div className={cn(styles.container, className)} data-section="mfa-setup">
      {currentCodeDescription || description}

      {!currentCodeDescription && (
        <>
          <div className={styles.authenticator}>
            {isLoading || !otpauthURI ? (
              <Skeleton
                className={styles.qrPlaceholder}
                width="180px"
                height="180px"
                borderRadius="12px"
              />
            ) : (
              <div className={styles.qr}>
                <QRCodeSVG value={otpauthURI} size={QR_CODE_SIZE} level="M" />
              </div>
            )}

            <div className={styles.instructions}>
              <span className="p3b">
                <FormattedMessage
                  id="mfa.setup.apps"
                  defaultMessage="Google Authenticator, 1Password, Authy or any TOTP app"
                />
              </span>

              <p className={cn(styles.account, 'p3t')}>
                <FormattedMessage
                  id="mfa.setup.accountHint"
                  defaultMessage="The app will show a new 6-digit code every 30 seconds for “{issuer} — {account}”."
                  values={{ issuer, account }}
                />
              </p>

              {collapsibleSetupKey && (
                <Button
                  className={styles.manualToggle}
                  variant="tertiary"
                  size="small"
                  onClick={() => setIsSetupKeyVisible(!isSetupKeyVisible)}
                >
                  {isSetupKeyVisible ? (
                    <FormattedMessage
                      id="mfa.setup.hideKey"
                      defaultMessage="Hide setup key"
                    />
                  ) : (
                    <FormattedMessage
                      id="mfa.setup.showKey"
                      defaultMessage="Can’t scan the QR code? Enter the key manually"
                    />
                  )}
                </Button>
              )}

              {isSetupKeyVisible && secret && (
                <div className={styles.setupKey}>
                  <span className={cn(styles.setupKeyValue, 'p3b')}>
                    {formatSetupKey(secret)}
                  </span>

                  <Button
                    variant="tertiary"
                    size="small"
                    onClick={() => copyToClipboard(secret)}
                  >
                    <FormattedMessage id="global.copy" defaultMessage="Copy" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className={styles.divider} />
        </>
      )}

      <MFACodeField
        value={code}
        onChange={onCodeChange}
        onComplete={onCodeComplete}
        errorMessage={errorMessage}
        disabled={disabled || isLoading}
        label={
          <FormattedMessage
            id="mfa.setup.codeLabel"
            defaultMessage="Code from the app"
          />
        }
      />
    </div>
  );
};
